import { useState, useCallback, useEffect } from "react";
import {
  Box, Grid, TextField, Button, Typography, MenuItem, Select,
  FormControl, InputLabel, Alert, Snackbar
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { updateSEO } from "../utils/SEO";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    serviceType: "",
    budget: "",
    deadline: "",
    referral: "",
    projectDetails: "",
    botField: "", // Honeypot field for spam protection
  });

  const navigate = useNavigate();

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s-()]{10,}$/;
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    // Service type validation
    if (!formData.serviceType) {
      newErrors.serviceType = "Please select a service type";
    }

    // Budget validation
    if (!formData.budget) {
      newErrors.budget = "Please select a budget range";
    }

    // Project details validation
    if (!formData.projectDetails.trim()) {
      newErrors.projectDetails = "Please provide project details";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  }, [errors]);

  // Encode form data for Netlify Forms submission
  const encode = (data) => {
    return Object.keys(data)
      .map((key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key])))
      .join('&');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Check honeypot field - if filled, it's a bot
    if (formData.botField) {
      // Silently fail for bots
      return;
    }

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields correctly",
        severity: "error"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data for Netlify Forms
      const formPayload = {
        'form-name': 'contact',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        serviceType: formData.serviceType,
        budget: formData.budget,
        deadline: formData.deadline || '',
        referral: formData.referral,
        projectDetails: formData.projectDetails,
      };

      // Submit to Netlify Forms - must submit to root for SPA routing
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(formPayload),
      });

      // Netlify Forms returns 200 on success (even if HTML) or 404 if form not found
      if (response.status === 404) {
        throw new Error('Form not found. Please ensure the form is properly configured on Netlify. Make sure you have deployed the site with the hidden form in index.html.');
      }

      // Check if response is successful (200-299 range)
      if (response.status >= 200 && response.status < 300) {
        // Success - Netlify Forms returns HTML on success
        setSnackbar({
          open: true,
          message: "Message sent successfully! We'll be in touch soon.",
          severity: "success"
        });
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Reset form data
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        serviceType: "",
        budget: "",
        deadline: "",
        referral: "",
        projectDetails: "",
        botField: "",
      });

      // Navigate to thank you page after a short delay
      setTimeout(() => {
        navigate("/thankyou");
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error: ${error.message}. Please try again or contact us directly.`,
        severity: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    updateSEO({
      title: "Contact Nicole Hahn Designs | Schedule Your Interior Design Consultation | Seattle, WA",
      description: "Contact Nicole Hahn Designs for luxury interior design services in Seattle, Bellevue, Tacoma, and surrounding areas. Schedule your discovery call or consultation today. Email: info@nicolehahndesigns.com",
      url: "https://nicolehahndesigns.com/contact",
      image: "https://nicolehahndesigns.com/livingMain.jpg"
    });

    // Add enhanced structured data for Contact page
    const contactScript = document.createElement('script');
    contactScript.type = 'application/ld+json';
    contactScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Nicole Hahn Designs",
      "description": "Contact Nicole Hahn Designs for luxury interior design services in Seattle, Bellevue, Tacoma, and surrounding areas. Schedule your discovery call or consultation today.",
      "url": "https://nicolehahndesigns.com/contact",
      "mainEntity": {
        "@type": "HomeAndConstructionBusiness",
        "name": "Nicole Hahn Designs",
        "email": "info@nicolehahndesigns.com",
        "url": "https://nicolehahndesigns.com",
        "areaServed": [
          {
            "@type": "Place",
            "name": "Seattle, WA"
          },
          {
            "@type": "Place",
            "name": "Bellevue, WA"
          },
          {
            "@type": "Place",
            "name": "Tacoma, WA"
          },
          {
            "@type": "Place",
            "name": "King County, WA"
          },
          {
            "@type": "Place",
            "name": "Pierce County, WA"
          }
        ],
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "info@nicolehahndesigns.com",
            "availableLanguage": ["English"],
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "17:00"
            }
          }
        ]
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://nicolehahndesigns.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact",
            "item": "https://nicolehahndesigns.com/contact"
          }
        ]
      }
    });
    document.head.appendChild(contactScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        const text = script.text;
        if (text.includes('"@type": "ContactPage"')) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        backgroundColor: "#e6e4df",
        paddingBottom: "5rem",
      }}
    >
      {/* Full-Width Image */}
      <Box
        component="img"
        src="/livingMain.jpg"
        alt="Contact Nicole Hahn Designs for luxury interior design services in Seattle, Bellevue, Tacoma, and surrounding areas - Custom homes, remodels, bathroom, and kitchen design"
        loading="eager"
        width="1920"
        height="800"
        sx={{
          width: "100%",
          height: { xs: "250px", md: "1000px" },
          objectFit: "cover",
          objectPosition: "center 90%",
          backgroundColor: "#e6e4df"
        }}
      />

      {/* Content Section */}
      <Grid
        container
        spacing={4}
        sx={{
          width: "90%",
          margin: { xs: "2rem auto", md: "3rem auto" },
        }}
      >
        {/* Left Column - Text */}
        <Grid item xs={12} md={6} sx={{
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: { xs: 300, sm: 300, md: 700 },
              marginBottom: "1rem",
              textAlign: "left",
              width: "80%",
              fontFamily: '"Bebas Neue", sans-serif'
            }}
          >
            We look forward to hearing from you!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "18px",
              maxWidth: "80%",
              textAlign: "left",
              fontFamily: '"Old Standard TT", serif',
              fontWeight: { xs: 300, sm: 300, md: 400 }
            }}
          >
            To find out more about our design services and to get your discovery call scheduled, please fill out the contact form.<br /><br />
            You can also reach us directly at:
            <Box
              sx={{
                width: "100px",
                height: "2px",
                backgroundColor: "#000000",
                margin: "0.5rem 0"
              }}
            />
            <a href="mailto:info@nicolehahndesigns.com" style={{
              margin: "0.5rem 0",
              color: "#000000",
              textDecoration: "none"
            }}>info@nicolehahndesigns.com</a>
          </Typography>
        </Grid>

        {/* Right Column - Contact Form */}
        <Grid item xs={12} md={6} sx={{ padding: "1rem" }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            aria-label="Contact form"
            data-netlify="true"
            name="contact"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "2rem", md: "1.2rem" },
              backgroundColor: "#e6e4df",
              padding: { xs: "1.5rem", md: "2rem" },
              borderRadius: "8px",
              maxWidth: "100%",
              position: "relative",
            }}
          >
            {/* Hidden input for Netlify form detection */}
            <input type="hidden" name="form-name" value="contact" />

            {/* Honeypot field for spam protection - hidden from users */}
            <input
              type="text"
              name="botField"
              value={formData.botField}
              onChange={handleChange}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                opacity: 0,
                pointerEvents: 'none',
              }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            {/* Text Fields */}
            {[
              { label: "Full Name", name: "name", type: "text" },
              { label: "Email", name: "email", type: "email" },
              { label: "Phone Number", name: "phone", type: "tel" },
              { label: "Project Address", name: "address", type: "text" },
            ].map((field) => (
              <TextField
                key={field.name}
                label={field.label}
                name={field.name}
                type={field.type}
                value={formData[field.name]}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors[field.name]}
                helperText={errors[field.name]}
                InputLabelProps={{
                  shrink: true,
                  style: { whiteSpace: 'normal' }
                }}
                sx={{
                  backgroundColor: "#F5F5F5",
                  borderRadius: "6px",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#999",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#333",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    lineHeight: "1.2",
                    whiteSpace: "normal",
                    maxWidth: "100%"
                  }
                }}
              />
            ))}

            {/* Dropdowns */}
            {[
              {
                label: "Which service best describes what you are looking for?",
                name: "serviceType",
                options: [
                  "Decor, Furniture, Fixtures, and/or Lighting Selections",
                  "Full Service or Remodels",
                  "Design to implement on your own",
                  "Two Hour Design or Color Consultation",
                  "Other",
                ],
              },
              {
                label: "Project Budget (not including design fees)",
                name: "budget",
                options: [
                  "$30k - $50k",
                  "$50k - $100k",
                  "$100k - $200k",
                  "$200k - $500k",
                  "$500k+",
                  "$10,000 - I am interested in Design Day",
                  "Other - I am interested in a consultation or styling",
                ],
              },
              {
                label: "How did you hear about Nicole Hahn Designs?",
                name: "referral",
                options: ["Google", "Facebook", "Instagram", "Client Referral", "Industry Referral", "Other"],
              },
            ].map((dropdown) => (
              <FormControl
                fullWidth
                key={dropdown.name}
                error={!!errors[dropdown.name]}
                sx={{
                  backgroundColor: "#e6e4df",
                  borderRadius: "6px",
                  "& .MuiInputLabel-root": {
                    lineHeight: "1.2",
                    whiteSpace: "normal",
                    maxWidth: "100%"
                  }
                }}
              >
                <InputLabel
                  sx={{
                    whiteSpace: "normal",
                    position: "relative",
                    transform: "none",
                    marginBottom: "8px"
                  }}
                >
                  {dropdown.label}
                </InputLabel>
                <Select
                  name={dropdown.name}
                  value={formData[dropdown.name]}
                  onChange={handleChange}
                  label={dropdown.label}
                  required
                  sx={{
                    backgroundColor: "#F5F5F5",
                    borderRadius: "6px",
                    marginTop: "16px"
                  }}
                >
                  {dropdown.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors[dropdown.name] && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {errors[dropdown.name]}
                  </Typography>
                )}
              </FormControl>
            ))}

            {/* Date Picker */}
            <FormControl
              fullWidth
              error={!!errors.deadline}
              sx={{
                backgroundColor: "#e6e4df",
                borderRadius: "6px",
                "& .MuiInputLabel-root": {
                  lineHeight: "1.2",
                  whiteSpace: "normal",
                  maxWidth: "100%"
                }
              }}
            >
              <InputLabel
                sx={{
                  whiteSpace: "normal",
                  position: "relative",
                  transform: "none",
                  marginBottom: "8px"
                }}
              >
                Do you have a hard deadline for the completion of your project? (Optional)
              </InputLabel>
              <TextField
                name="deadline"
                type="date"
                value={formData.deadline}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: "#F5F5F5",
                  borderRadius: "6px",
                  marginTop: "16px",
                  "& .MuiInputLabel-root": {
                    display: "none"
                  }
                }}
              />
              {errors.deadline && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  {errors.deadline}
                </Typography>
              )}
            </FormControl>

            {/* Textarea for Additional Project Details */}
            <TextField
              label="Tell us a bit more about your project"
              name="projectDetails"
              value={formData.projectDetails}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              required
              error={!!errors.projectDetails}
              helperText={errors.projectDetails}
              sx={{
                backgroundColor: "#F5F5F5",
                borderRadius: "6px",
              }}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{
                fontSize: "16px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: isSubmitting ? "#999" : "#464d22",
                "&:hover": {
                  backgroundColor: isSubmitting ? "#999" : "#757a5c",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                },
                transition: "all 0.3s ease-in-out",
              }}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
